import React, { FC } from 'react';
import { useStyles } from './AnotherOfferInvolvedBlockPreview.styles';
import { Grid, Paper, Stack, Typography } from '@mui/material';
import { FlagFormFields } from '../../../../../../components/FlagForm/constants/FlagFormFields';
import { FlagData } from '@modules/HappinessModule/interfaces/FlagData';
import { yesOrNoByBoolean } from '@shared/helpers/yesOrNoByBoolean';
import { FlagFieldBlockPreview } from '../FlagFieldBlockPreview';
import { formatFinancialValue } from '@modules/EvaluationModule/pages/UserCompensationPage/helpers/currencyOptions';
import compact from 'lodash/compact';
import { NOT_SETTED_VALUE_MSG } from '@modules/HappinessModule/pages/FlagPage/constants/constants';
import { compensationCompetitivenessOptions } from '@modules/HappinessModule/pages/FlagPage/components/FlagForm/constants/constants';
import { FlagPermissionsData } from '@modules/HappinessModule/pages/FlagPage/interfaces/FlagPermissionsData';

interface Props {
  flagData: FlagData;
  userFlagPermissions: FlagPermissionsData;
}

const NOT_PROVIDED = 'Not provided';

const benefitFields = [
  FlagFormFields.pto_incl,
  FlagFormFields.paid_holidays,
  FlagFormFields.parental_leave,
  FlagFormFields.health_ins,
  FlagFormFields.wellness,
  FlagFormFields.prof_dev,
  FlagFormFields.thirteenth_salary,
  FlagFormFields.stock_opt,
  FlagFormFields.bonuses,
];

export const AnotherOfferInvolvedBlockPreview: FC<Props> = ({ flagData, userFlagPermissions }) => {
  const styles = useStyles();
  const { flagFields } = flagData;

  const competitivenessLabel =
    compensationCompetitivenessOptions.find((o) => o.value === flagFields.compensationCompetitiveness)?.description ??
    NOT_SETTED_VALUE_MSG;

  const amount = formatFinancialValue(flagFields.compensation);
  const currency = flagFields.currencyTypeNotProvided ? NOT_PROVIDED : flagFields.currencyType;
  const amountType = flagFields.amountTypeNotProvided ? NOT_PROVIDED : flagFields.amountType;
  const amountWithCurrency = compact([amount, currency]).join(' ');

  const compensationValue = flagFields.compensationNotProvided
    ? NOT_PROVIDED
    : !amount
    ? NOT_SETTED_VALUE_MSG
    : compact([amountWithCurrency, amountType]).join(' / ');

  return (
    <Paper variant="elevation" elevation={2}>
      <Grid
        container
        className={
          userFlagPermissions.compensationDetails.isReadable
            ? styles.anotherOfferInvolvedBlockHrView
            : styles.mainContainer
        }
        rowGap="36px"
      >
        <Grid item xs={12}>
          <Typography variant="h2">
            {flagFields.isAnotherOfferInvolved
              ? FlagFormFields.isAnotherOfferInvolved.label
              : 'No other offer is involved'}
          </Typography>
        </Grid>

        {userFlagPermissions.compensationDetails.isReadable && flagFields.isAnotherOfferInvolved && (
          <>
            {/* Company and employment type */}
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <FlagFieldBlockPreview
                    label={FlagFormFields.companyName.label}
                    value={
                      flagFields.companyNameNotProvided ? NOT_PROVIDED : flagFields.companyName ?? NOT_SETTED_VALUE_MSG
                    }
                  />
                </Grid>
                <Grid item xs={4}>
                  <FlagFieldBlockPreview
                    label={FlagFormFields.hiringType.label}
                    value={
                      flagFields.hiringTypeNotProvided ? NOT_PROVIDED : flagFields.hiringType ?? NOT_SETTED_VALUE_MSG
                    }
                  />
                </Grid>
                <Grid item xs={4}>
                  <FlagFieldBlockPreview
                    label={FlagFormFields.role.label}
                    value={flagFields.roleNotProvided ? NOT_PROVIDED : flagFields.role ?? NOT_SETTED_VALUE_MSG}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Compensation */}
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <FlagFieldBlockPreview label={FlagFormFields.compensation.label} value={compensationValue} />
                </Grid>
                <Grid item xs={4}>
                  <FlagFieldBlockPreview
                    label={FlagFormFields.compensationCompetitiveness.label}
                    value={flagFields.compensationCompetitiveness ? competitivenessLabel : NOT_SETTED_VALUE_MSG}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Offer status */}
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <FlagFieldBlockPreview
                    label={FlagFormFields.offerAcceptanceStatus.label}
                    value={flagFields.offerAcceptanceStatus ?? NOT_SETTED_VALUE_MSG}
                  />
                </Grid>
                {flagFields.offerAcceptanceStatus === 'rejected' && (
                  <Grid item xs={8}>
                    <FlagFieldBlockPreview
                      label={FlagFormFields.offerRejectionReason.label}
                      value={flagFields.offerRejectionReason ?? NOT_SETTED_VALUE_MSG}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>

            {/* Benefits and bonuses */}
            <Grid item xs={12}>
              {flagFields.benefitsNotProvided ? (
                <FlagFieldBlockPreview label="Benefits and bonuses" value={NOT_PROVIDED} />
              ) : (
                <>
                  <Grid container spacing={2}>
                    {benefitFields.map(({ name, label }) => (
                      <Grid item xs={4} key={name}>
                        <FlagFieldBlockPreview label={label} value={yesOrNoByBoolean(!!flagFields[name])} />
                      </Grid>
                    ))}
                  </Grid>
                  {flagFields.benefits && (
                    <Stack sx={{ marginTop: 2 }}>
                      <FlagFieldBlockPreview label={FlagFormFields.benefits.label} value={flagFields.benefits} />
                    </Stack>
                  )}
                </>
              )}
            </Grid>
          </>
        )}
      </Grid>
    </Paper>
  );
};
